/**
 * Traditional Chinese (zh-Hant) dictionary.
 *
 * Typed as `Messages`, so omitting a key or misspelling one is a compile error
 * rather than an English string leaking into a Chinese interface.
 *
 * Register: written Traditional Chinese as used in Taiwan and Hong Kong for
 * consumer food ordering. Restaurant-facing copy is slightly more formal than
 * customer-facing copy, which matches how the English reads.
 *
 * Chinese has a single plural category, so `one` and `other` are identical
 * throughout. They are kept as separate keys rather than collapsed because the
 * English dictionary defines the structure, and a locale that *does* inflect
 * (Japanese counters, for instance) can then vary them.
 */

import type { Messages } from './en';

export const zhHant: Messages = {
  common: {
    appName: 'Yummy',
    save: '儲存',
    saveChanges: '儲存變更',
    cancel: '取消',
    close: '關閉',
      pickRangeStart: '請選擇第一天。',
      pickRangeEnd: '再選擇最後一天。',
    delete: '刪除',
    remove: '移除',
    edit: '編輯',
    add: '新增',
    back: '返回',
    next: '下一步',
    retry: '重試',
    justAMoment: '請稍候。',
    refresh: '重新整理',
    refreshing: '重新整理中…',
    loading: '載入中…',
    somethingWentWrong: '發生錯誤',
    notFound: '找不到頁面',
    optional: '選填',
    required: '必填',
    today: '今天',
    previousMonth: '上個月',
    nextMonth: '下個月',
    tomorrow: '明天',
    yesterday: '昨天',
    closed: '休息中',
    open: '營業中',
    openNow: '營業中',
    openInMaps: '在 Google 地圖開啟 {address}',
    callRestaurant: '致電 {phone}',
    search: '搜尋',
    all: '全部',
    none: '無',
    yes: '是',
    no: '否',
    of: '/',
    and: '與',
    or: '或',
    done: '完成',
    confirm: '確認',
    dismiss: '關閉',
    copy: '複製',
    copied: '已複製',
    print: '列印',
    keep: '保留',
    show: '顯示',
    hide: '隱藏',
    name: '名稱',
    description: '說明',
    phone: '電話',
    email: '電子郵件',
    address: '地址',
    note: '備註',
    notes: '備註',
    price: '價格',
    quantity: '數量',
    total: '總計',
    subtotal: '小計',
    discount: '折扣',
    tax: '稅金',
    currency: '貨幣',
    password: '密碼',
    country: '國家',
    timezone: '時區',
    date: '日期',
    time: '時間',
    hour: '小時',
    minute: '分鐘',
    status: '狀態',
    details: '詳細資料',
    actions: '操作',
    settings: '設定',
    languageAndAppearance: '語言與外觀',
    language: '語言',
    appearance: '外觀',
    themeLight: '淺色',
    themeDark: '深色',
    themeSystem: '跟隨系統',
    themeSystemHint: '依照裝置設定',
    localeSystem: '跟隨裝置語言',
    localeSystemShort: '跟隨裝置',
    switchLanguage: '切換語言',
    switchToDark: '切換為深色主題',
    switchToLight: '切換為淺色主題',
    overnightSuffix: '（+1 天）',
    itemCount: { one: '{count} 件餐點', other: '{count} 件餐點' },
    orderCount: { one: '{count} 筆訂單', other: '{count} 筆訂單' },
  },

  time: {
    ready: '可取餐',
    anyMoment: '隨時可取餐',
    underAMinute: '不到一分鐘',
    aboutMinutes: '約 {count} 分鐘',
    aboutHours: '約 {hours} 小時',
    aboutHoursMinutes: '約 {hours} 小時 {minutes} 分',
  },

  units: {
    secondShort: '秒',
    minuteShort: '分',
    hourShort: '時',
    valueUnit: '{value}{unit}',
    pair: '{first} {second}',
    secondsOnly: '{value} 秒',
    minutesOnly: '{value} 分',
    minutesSeconds: '{minutes} 分 {seconds} 秒',
    hoursOnly: '{value} 小時',
    hoursMinutes: '{hours} 小時 {minutes} 分',
    notMeasured: '—',
  },

  relative: {
    ago: '{time}前',
    inFuture: '{time}後',
  },

  orderStatus: {
    pending: '新訂單',
    accepted: '已接單',
    preparing: '製作中',
    ready: '可取餐',
    completed: '已取餐',
    cancelled: '已取消',
  },

  customer: {
    landing: {
      tagline: '提前點餐，免排隊',
      intro: '掃描餐廳的 QR Code，用手機點餐，做好後到櫃檯取餐。',
      howItWorks: '使用方式',
      step1: '掃描桌上或櫃檯的 QR Code。',
      step2: '選擇餐點與取餐時間。',
      step3: '留下手機號碼，方便我們與你聯絡。',
      step4: '到櫃檯出示訂單編號，或讓我們掃描條碼。',
      haveOrder: '已經有訂單了嗎？',
      haveOrderHint: '輸入收據或櫃檯螢幕上的六位數訂單編號。',
      orderCode: '訂單代碼',
      findOrder: '查詢我的訂單',
      openedByMistake: '不小心打開了嗎？',
      openedByMistakeHint: '輸入餐廳網址，或貼上另一個 QR Code 的連結。',
      restaurant: '餐廳',
      openMenu: '開啟菜單',
      pickupOnly: '僅限自取',
      pickupOnlyHint: 'Yummy 餐廳不提供外送。所有餐點皆須親臨取餐，這也是快速的原因。',
      previousOrders: '我的歷史訂單',
    },

    menu: {
      chooseGroup: '請選擇{group}',
      done: '完成',
      change: '更改',
      changeChoices: '更改選項',
      minimumOrder: '最少 {count}',
      orderingFrom: '取餐位置：{table}',
      loading: '載入菜單…',
      sessionFailed: '無法啟動你的工作階段',
      sessionStarting: '正在準備',
      other: '其他',
      notTakingOrders: '目前暫停接單',
      notTakingOrdersHint: '這間餐廳已暫停線上點餐，請直接洽詢櫃檯。',
      closedNow: '目前休息中',
      closedScheduleHint: '你仍然可以預約 {when} 取餐。',
      closedCheckBack: '請於營業時間再回來看看。',
      pickupOnlyHours: '僅限自取 · {hours}',
      gone: '這間餐廳已不再提供服務。',
      myOrders: '我的訂單',
      empty: '菜單尚無餐點',
      emptyHint: '這間餐廳尚未發布任何餐點，請洽櫃檯人員。',
      soldOut: '已售完',
      // 餐點選項面板。
      addItem: '加入 {item}',
      addToOrder: '加入訂單',
      basePrice: '基本價格',
      breakdownBase: '{total} 基本',
      breakdownExtras: '{count} 項加購 {total}',
      chooseOne: '選擇 1 項',
      pickUpTo: '最多選 {count} 項',
      required: '必選',
      optional: '選填',
      included: '已包含',
      onlyLeft: '僅剩 {count} 份',
      availableFrom: '供應時間 {day} {from}–{to}',
      unavailableNow: '目前不供應',
      pricesNote: '僅限自取。價格以 {currency} 計{taxNote}。',
      taxNote: '，未含 {rate}% 稅金',
      offerEnds: '優惠於 {when} 結束',
      onOffer: '優惠中',
      scheduleHint: '你可以在結帳時預約取餐時間',
      loadFailed: '無法載入菜單',
      loadFailedHint: '菜單目前無法使用。',
      switchRestaurant: '你的訂單包含其他餐廳的餐點。要在這裡重新開始點餐嗎？',
    },

    verified: {
      mark: '已驗證餐廳 — 了解詳情',
      title: '已驗證餐廳',
      reason: 'Yummy 已核對這間餐廳的名稱、地址與聯絡方式。',
      since: '自 {when} 起已驗證。',
    },

    cart: {
      review: '確認訂單',
    },

    checkout: {
      screenTitle: '結帳',
      myOrders: '我的訂單',
      phoneShort: '以 {dial} 來說太短 — 本地號碼為 {min} 位。',
      phoneLong: '以 {dial} 來說太長 — 本地號碼最多 {max} 位。',
      phoneForeign: '這看起來是 {dial} 號碼。餐廳可能需要打國際電話。',
      elsewhereTitle: '此訂單需在 {country} 自取',
      elsewhere: '你目前似乎位於 {country}。訂單需到店自取，請問能前來嗎？',
      title: '你的訂單',
      loading: '載入結帳資訊…',
      unavailable: '目前無法結帳',
      pickupTime: '取餐時間',
      asap: '盡快取餐',
      asapHint: '做好即可取餐',
      asapClosed: '目前休息中',
      schedule: '預約時間',
      scheduleHint: '選擇取餐時間',
      estimatedReady: '預計可取餐時間 {when}',
      estimateInitial: '（初步估計）',
      estimateLearned: '（根據 {count} 筆相似訂單）',
      chooseTime: '請選擇取餐時間',
      yourDetails: '你的資料',
      yourDetailsHint: '這些資料僅用於製作與交付你的訂單。',
      nameLabel: '姓名',
      namePlaceholder: '取餐人姓名',
      phoneLabel: '手機號碼',
      phonePlaceholder: '+886 912 345 678',
      phoneHint: '必填，方便餐廳就這筆訂單與你聯絡。',
      emailLabel: '電子郵件',
      emailPlaceholder: 'you@example.com',
      emailHint: '選填。填寫後我們會將收據寄給你。',
      noteLabel: '給廚房的備註',
      notePlaceholder: '過敏原、不要辣、不需要餐具…',
      payment: '付款',
      payAtCounter: '到櫃檯付款',
      payAtCounterHint: '此訂單未線上付款。取餐時請出示訂單編號。',
      placeOrder: '送出訂單',
      placing: '送出中…',
      orderPlacedWithReceipt: '訂單 {number} 已成立 — 收據將寄至 {email}',
      orderPlaced: '訂單 {number} 已成立',
      noSlots: '目前沒有可預約的取餐時段',
      noSlotsHint: '這間餐廳下週已額滿或休息中。可以改用「盡快取餐」。',
      slotTimesAvailable: '有 {count} 個時段可選',
      findingSlots: '尋找取餐時段…',
      slotTooSoonChip: '{time}，對這張訂單來說太趕',
      slotTooSoonKey: '對這張訂單來說太趕',
      slotTooSoonTitle: '這個取餐時間太趕',
      slotTooSoonMessage: '這張訂單通常需要約 {minutes} 分鐘，廚房可能無法及時完成。',
      slotTooSoonAction: '改用 {time}',
    },

    order: {
      screenTitle: '訂單',
      loading: '載入你的訂單…',
      loadFailed: '無法載入這筆訂單',
      loadFailedHint: '這筆訂單可能已被移除。',
      notFound: '找不到訂單',
      notFoundByCode: '找不到編號為 {code} 的訂單。',
      goBack: '返回',
      scheduled: '預約取餐',
      pending: '等待餐廳確認',
      accepted: '餐廳已接單',
      preparing: '製作中',
      readyTitle: '可取餐',
      collected: '已取餐 — 請享用！',
      cancelled: '訂單已取消',
      pickupTime: '取餐時間',
      pickupTimeHint: '我們屆時會為你準備好。',
      estimatedReady: '預計可取餐',
      readyNow: '可取餐',
      anyMomentNow: '即將可取',
      lateBy: '比預計遲{when}',
      runningBehind: '你的訂單會稍為延遲，我們正在處理。',
      runningLate: '比預計時間遲。',
      notAccepted: '此訂單尚未獲接單。',
      notAcceptedSoon: '這個取餐時間太趕。廚房尚未接單。',
      callForUpdate: '致電店家查詢',
      comeToCounter: '請至櫃檯取餐',
      comeToCounterHint: '出示下方的 QR Code，或告知我們你的訂單編號。',
      showWhenCollecting: '取餐時請出示',
      showWhenCollectingHint: '餐點準備好時，我們會叫這個號碼。',
      orderNumber: '訂單 #{number}',
      codeLabel: '編號 {code} — 供店員輸入',
      summaryLine: {
        one: '{count} 件餐點',
        other: '{count} 件餐點',
      },
      discountApplied: '已套用{label}',
      paid: '已付款',
      cancelReasonCustomer: '顧客自行取消',
      stepPlaced: '已收到訂單',
      stepAccepted: '餐廳已接單',
      stepPreparing: '製作中',
      stepReady: '可取餐',
      stepCompleted: '已取餐',
      stepCancelled: '已取消',
      whatYouOrdered: '你點的餐點',
      yourNote: '你的備註',
      pickupFrom: '取餐地點',
      receiptSent: '收據已寄至 {email}。',
      backToMenu: '返回菜單',
      myOrders: '我的訂單',
      cancelOrder: '取消訂單',
      collectedIt: '我已取餐',
      cancelTitle: '要取消這筆訂單嗎？',
      cancelMessage: '餐廳尚未開始製作。取消後無法復原。',
      cancelConfirm: '取消訂單',
      cancelKeep: '保留訂單',
      cancelledToast: '訂單已取消',
      readyToast: '你的餐點已可取餐！',
      collectedToast: '已取餐 — 請享用。',
      cancelledByStaffToast: '這筆訂單已被取消。',
    },

    history: {
      title: '我的訂單',
      loading: '載入你的訂單…',
      empty: '尚無訂單',
      emptyHint: '掃描餐廳的 QR Code 即可送出第一筆訂單。',
      enterCode: '輸入編號',
      forScheduled: '取餐時間 {when}',
      placedAt: '下單時間 {when}',
    },
  },

  merchant: {
    pickup: {
      loading: '正在查詢訂單…',
      notFoundTitle: '此代碼沒有對應訂單',
      notFound: '此代碼可能屬於其他餐廳，或訂單已完成。',
    },
    nav: {
      dashboard: '儀表板',
      orders: '訂單',
      cook: '待製作',
      menu: '菜單',
      hours: '營業時間',
      capacity: '產能',
      tables: '桌位',
      settings: '設定',
      merchant: '商家',
      restaurant: '餐廳',
      signOut: '登出',
      collapse: '收合側邊欄',
      expand: '展開側邊欄',
    },

    connection: {
      live: '已連線',
      connecting: '連線中',
      offline: '重新連線中',
      newCount: '· {count} 筆新訂單',
    },

    auth: {
      signInTitle: '商家登入',
      signUpTitle: '建立餐廳帳號',
      signInIntro: '管理訂單、菜單與今日營業額。',
      signUpIntro: '設定菜單與營業時間，開始接受自取訂單。',
      emailPlaceholder: 'owner@restaurant.com',
      passwordLabel: '密碼',
      passwordPlaceholder: '至少 8 個字元',
      passwordHint: '至少 8 個字元。',
      signIn: '登入',
      signUp: '建立帳號',
      haveAccount: '我已經有帳號',
      needAccount: '建立新帳號',
      checkInbox: '請查看你的信箱',
      continueWithGoogle: '使用 Google 繼續',
      orDivider: '或',
      googleUnavailable: '此餐廳尚未開放 Google 登入。',
      confirmationSent: '我們已將確認連結寄至 {email}。請確認後再登入。',
      terms: '繼續即表示你同意僅將 Yummy 用於合法的餐飲點餐用途。',
      loading: '正在檢查你的登入狀態。',
      loadingTitle: '載入中',
    },

    onboarding: {
      title: '設定你的餐廳',
      intro: '這些設定之後都可以修改。營業時間預設為每日 09:00–21:00。',
      basics: '基本資料',
      restaurantName: '餐廳名稱',
      restaurantNamePlaceholder: '我的餐廳',
      webAddress: '網址代稱',
      webAddressPlaceholder: 'my-restaurant',
      webAddressHint: '用於 QR Code 與連結。請使用小寫英文字母、數字與連字號。',
      descriptionPlaceholder: '顧客點餐前應該知道什麼？',
      locationAndMoney: '地點與金額',
      locationAndMoneyHint: '營業時間與每日營業額皆以此時區計算。',
      currencyLabel: '貨幣',
      addressHint: '會顯示在顧客的訂單上，讓他們知道到哪裡取餐。',
      pickupOnly: '僅限自取',
      pickupOnlyHint: 'Yummy 不提供外送。顧客會提前點餐並至櫃檯取餐。',
      create: '建立餐廳',
      noRestaurant: '尚未選擇餐廳',
      noRestaurantHint: '建立餐廳後即可查看儀表板。',
    },

    dashboard: {
      title: '儀表板',
      netSales: '淨銷售額',
      netSalesHint: '總額 {gross}，折扣 {discount}',
      orders: '訂單',
      ordersHint: '{completed} 筆已取餐 · {open} 筆進行中',
      averageOrder: '平均客單價',
      averageOrderHint: '根據 {count} 筆已取餐訂單',
      onTimeRate: '準時率',
      onTimeHint: '{measured} 筆中有 {onTime} 筆早於預計時間',
      onTimeNone: '尚無已取餐訂單可供計算',
      typicalWait: '一般等候時間',
      typicalWaitHint: '10 筆中有 9 筆低於 {duration}',
      cancellations: '取消率',
      cancellationsHint: '{placed} 筆訂單中有 {cancelled} 筆取消',
      fulfilment: '出餐效率',
      fulfilmentHint: '從下單到交付，每筆訂單的時間實際花在哪裡。',
      stageSitting: '等待接單',
      stageSittingHint: '下單 → 接單。這裡過長代表訂單沒有被注意到。',
      stageKitchen: '廚房製作',
      stageKitchenHint: '接單 → 可取餐。這是廚房能掌握的區段。',
      stageHandover: '櫃檯等待',
      stageHandoverHint: '可取餐 → 已取餐。這裡過長代表顧客沒有被告知。',
      estimateAccuracy: '預估準確度',
      estimateAccuracyHint: '結帳時告知的預計時間與實際可取餐時間的平均差距。數值越低越好。',
      completedRate: '完成率',
      whereOrdersCame: '訂單進單時段',
      whereOrdersCameHint: '營業日各小時的訂單量。',
      scheduled: '預約',
      walkUp: '現場',
      scheduledShare: '為預約',
      scheduledVsWalkupHint: '事先預約，或現場點餐。',
      sales: '銷售',
      salesHint: '每日淨銷售額（{currency}）。',
      salesHourHint: '營業日各小時的淨銷售額（{currency}）。',
      salesMonthHint: '每月淨銷售額（{currency}）。',
      salesPeak: '高峰',
      bestSellers: '熱銷餐點',
      bestSellersHint: '依本期間已取餐數量排序。',
      etaLearning: '預估模型學習',
      etaLearningHint: '你的訂單顯示的備餐時間。',
      medianPrep: '備餐時間中位數',
      averagePrep: '平均備餐時間',
      learnedFrom: '筆訂單，來自近 {days} 天',
      slowestDishes: '最慢的餐點',
      biasLong: '預估時間比實際多 {duration}。',
      biasShort: '預估時間比實際少 {duration}。',
      biasWithin: '預估時間誤差在一分鐘內。',
      lastLearned: '最後一次學習：{when}。',
      slowestTen: '最慢的 10%',
      stillLearningHint:
        '在累積約十筆完成訂單之前，預估會以你為各餐點設定的備餐時間為準。目前已有 {count} 筆。',
      noHistory: '尚無預估歷史資料。',
      emptyPeriod: '本期間沒有訂單',
      emptyPeriodHint: '顧客開始點餐後，本頁會自動填入資料。可以試試「近 7 天」等較長的區間。',
      rangeToday: '今天',
      rangeYesterday: '昨天',
      rangeWeek: '近 7 天',
      rangeMonth: '本月',
      rangeLastMonth: '上個月',
      rangeYear: '今年',
      rangeLast28Days: '近 28 天',
      period: '統計期間',
    },

    cook: {
      title: '待製作',
      emptyTitle: '目前沒有待製作的餐點',
      emptyHint: '訂單進來時會顯示在這裡，標記為可取餐後就會消失。',
      // ---- 兩種模式 --------------------------------------------------------
      // 同一條隊列、兩種讀法：按訂單，因為要交付的是整張單；按批次，因為一個鑊
      // 同時煮得下好幾份。兩者都看「需完成時間」。
      byOrder: '按訂單',
      byBatch: '按批次',
      orderSummary: { one: '{count} 張單', other: '{count} 張單' },
      roundSummary: { one: '{count} 輪', other: '{count} 輪' },
      batchSummary: { one: '{count} 批', other: '{count} 批' },
      bandLater: '稍後',
      bandLateRule: '已過需完成時間',
      bandNowRule: '十分鐘內需完成',
      readyByLabel: '需完成',
      readyBy: '需於 {time} 完成',
      readyNow: '現在就要完成',
      inMinutes: { one: '{count} 分鐘後', other: '{count} 分鐘後' },
      lateBy: { one: '遲了 {count} 分鐘', other: '遲了 {count} 分鐘' },
      startBy: '{time} 前開始',
      scheduledStartBy: '已預約 · {time} 前開始',
      addedCount: { one: '新增 {count} 份', other: '新增 {count} 份' },
      lateAccepted: { one: '遲接 {count} 份', other: '遲接 {count} 份' },
      roundLateAccepted: { one: '遲接 {count} 份', other: '遲接 {count} 份' },
      roundReadyAt: '需於 {time} 完成',
      roundReadyNow: '現在就要完成 · {time}',
      // ---- 完成一輪 --------------------------------------------------------
      // 一輪是多張訂單的看法，所以完成它是把訂單自己的狀態轉換，一張一張寫下
      // 去：餐點做好了，顧客被通知來取餐。張數寫在按鈕上，因為那是一次點擊的
      // 影響範圍；只有在對話框有話要說時才出現——超過一張單，或有單是在這一輪
      // 開始後才接的。
      markRoundReady: { one: '標記 {count} 張單為可取餐', other: '標記 {count} 張單為可取餐' },
      markRoundReadyAria: {
        one: '標記 {count} 張單為可取餐 — 需於 {time} 完成',
        other: '標記 {count} 張單為可取餐 — 需於 {time} 完成',
      },
      markRoundReadyTitle: {
        one: '標記 {count} 張單為可取餐？',
        other: '標記 {count} 張單為可取餐？',
      },
      markRoundReadyMessage: '顧客會收到通知前來取餐，且無法復原。',
      markRoundReadyLateNote: {
        one: '這一輪開始後才接了 1 張單（{time}），也會一併標記為可取餐。',
        other: '這一輪開始後才接了 {count} 張單，也會一併標記為可取餐。',
      },
      markRoundReadyConfirm: { one: '標記 {count} 張為可取餐', other: '標記 {count} 張為可取餐' },
      roundReadyToast: { one: '已標記 {count} 張單為可取餐', other: '已標記 {count} 張單為可取餐' },
      // ---- 標記單一訂單為可取餐 --------------------------------------------
      // 按訂單檢視時每張單自己的動作。用字與訂單板相同——同一個動作，同一個說法；
      // 無障礙名稱補上訂單編號，因為對看不到卡片的人來說，這是一整排一模一樣的按鈕。
      markReady: '標記為可取餐',
      markReadyAria: '標記為可取餐 — 訂單 {number}',
      markFailedOne: '訂單 {number} 未能標記為可取餐。{reason}',
      markRoundFailed: '這一輪未能標記為可取餐。{reason}',
      // ---- 何時切下一輪 ----------------------------------------------------
      // 是建議，不是自動。要不要開鍋仍由商家決定：背後的兩個數字是他們自己的
      // 輪次時間窗與自己的批次中位數，句子會說明是哪一個觸發的。
      cutoffStartEnough: '現在就開一輪——已有 {count} 張單在等，夠一批了。',
      cutoffStartWaited: '現在就開一輪——最久的單已經等了 {waited}。',
      cutoffHold: '再等 {waited}——目前 {count} 張，還沒湊滿一輪。',
      cutoffBusy: '過去一小時已達這間廚房可消化量的 {percent}%。',
    },

    // ---- 產能 -------------------------------------------------------------
    // 用廚房自己的佇列來衡量它。所有實測數字都來自已記錄的批次，所以日誌空著時
    // 就直說，而不是把 0 當成讀數顯示。
    capacity: {
      title: '廚房產能',
      subtitle: '這間廚房來得及做多少，由它煮過的每一輪算出來。',

      logTitle: '紀錄說了什麼',
      logHint: '過去 {days} 天的每一批真實批次。',
      logEmpty: '還沒有任何批次紀錄。在「待製作」頁把一輪標記為可取餐，紀錄就從這裡開始。',
      logLearning: '目前只有 {count} 批——看得出趨勢，但還不足以盡信。煮得越多會越準。',
      logSummary: '批次 {batches} · 訂單 {orders} · 每輪通常 {size} 張。',
      batchWait: '等批次湊滿',
      batchWaitHint: '等鍋子裝滿的時間',
      capacityWait: '等廚房空出來',
      capacityWaitHint: '再快也沒辦法更早開始',
      cook: '烹調',
      cookHint: '實際加熱 · 平均 {mean}',

      arrivalsTitle: '訂單什麼時候來',
      arrivalsHint: '每小時訂單數，只計有單的那些日子平均。',
      arrivalsPeak: '最忙的是 {hour}:00，約 {count} 張單。',

      calcTitle: '這間廚房來得及嗎？',
      calcHint: '這裡不會儲存任何設定。改個數字，看看要多少才夠。',
      ordersAtOnce: '同時做幾張單',
      ordersAtOnceHint: '廚房可以同時做幾張單。',
      cookMinutes: '每張單需時（分鐘）',
      cookMinutesHint: '一張單占用一個爐位多久，從開始做到做好。',
      arrivals: '每小時來幾張單',
      arrivalsRateHint: '最忙的一小時約 {count} 張。',
      clearable: '每小時可消化',
      clearableHint: '同時張數 × 60 ÷ 每張分鐘',
      load: '負載',
      loadHint: '占可消化量的比例',
      wait: '加熱前的等待',
      waitHint: '在這個速率下的平均值。',
      singlePool:
        '這裡把廚房當成一個整體來算，沒有分開建模不同的爐位（炒鍋、蒸籠、油炸爐），所以無法指出是哪一個環節卡住。',
      cannotKeepUp: '消化不了',
      cannotKeepUpHint: '訂單來得比這間廚房能消化的速度還快。',
      verdictCalm: '從容。多數訂單幾乎馬上就開始煮。',
      verdictBusy: '忙碌。佇列真的在排隊，一有尖峰就會感覺到。',
      verdictCritical: '接近滿載。一點小尖峰就會變成漫長的等待。',
      verdictOver: '超出產能。從這裡開始，佇列只會越來越長。',

      windowTitle: '輪次時間窗',
      windowHint: '一輪開放多久，讓更多訂單加入。',
      windowCovers: '一輪涵蓋（分鐘）',
      windowBatch: '一輪幾張單',
      windowBatchHint: '在這個來單速率下。',
      windowAdds: '湊批增加',
      windowAddsHint: '平均約為時間窗的一半。',
      windowTradeoff: '時間窗越短，等待越少、批次越小；越長則鍋子裝得越滿，但第一張單要等更久。',
      windowSetting: '請到「設定」調整。',
    },

    orders: {
      noShows: '{count} 次未來取餐',
      collected: '{count} 次已取餐',
      // ---- 訂單板的兩個軸 -----------------------------------------------
      // 分頁只裝狀態，時限交給分區標題。任何可能同時屬於兩個分頁的條件，
      // 都只能是標籤或分區，不能變成第四個分頁。
      tabAll: '全部',
      tabAccept: '待接單',
      tabCooking: '製作中',
      tabReady: '可取餐',
      lateCount: { one: '{count} 筆延誤', other: '{count} 筆延誤' },
      readyCount: { one: '{count} 筆可取', other: '{count} 筆可取' },
      liveCount: '{count} 筆進行中',
      todayTitle: '今日',
      todaySubtitle: { one: '午夜至今 {count} 張單', other: '午夜至今 {count} 張單' },
      todayEmpty: '今日尚無完成的訂單',
      todayEmptyHint: '訂單完成或取消後會顯示在這裡。',
      todayNoMatch: '今日沒有符合的訂單',
      todayNoMatchHint: '請嘗試其他編號、姓名或電話。',
      todayShown: '{shown} / {total}',
      todayLoadMore: '載入更多',
      backToBoard: '返回訂單板',
      bandLate: '延誤',
      bandLateRule: '已超過應開始、完成或追蹤的時間',
      bandLateReadyRule: '放在櫃檯太久，該打電話了',
      bandNow: '現在',
      bandNowRule: '要立即處理，或十分鐘內到期',
      bandReadyNowRule: '接近追蹤時間，準備打電話',
      bandReadyNextRule: '放在櫃檯，還有時間',
      bandNext: '接下來',
      bandNextRule: '一小時內到期',
      sortLabel: '訂單排序',
      sortHint: '改變每個分組內卡片的先後次序。',
      sortUrgency: '按急迫程度',
      sortOldest: '先下單的先顯示',
      sortNewest: '後下單的先顯示',
      shelfBooked: '今日還有 {count} 張預約單',
      shelfNext: '下一張 {next}，最後一張 {last}',
      shelfNothing: '{duration} 內沒有到期訂單',
      bookedAt: '預約 {when}',
      readyBy: '預計 {when} 完成',
      placedAt: '{when} 下單',
      bookedInsidePrepChip: '預約時已來不及',
      stuck: '停滯 {duration}',
      cancelWhy: '為什麼取消？',
      cancelWhyHint: '只有「未來取餐」會記在顧客身上。另外兩項是店家自身的理由，不會出現在顧客紀錄中。',
      blockedToast: '該號碼將無法再向你下單。',
      blockFailedToast: '已取消，但無法封鎖該號碼。',
      cancelWithReason: '取消訂單',
      reasonNoShow: '未取餐',
      reasonSoldOut: '售完',
      reasonOther: '其他',
      blockNumber: '封鎖此號碼',
      blockedNote: '此號碼將無法向你下單。你可以在設定中解除封鎖。',
      fromElsewhere: '從 {country} 下單',
      title: '訂單',
      searchPlaceholder: '搜尋編號、姓名或電話',
      scanCode: '掃描取餐碼',
      scanTitle: '掃描取餐碼',
      scanHint: '將鏡頭對準顧客訂單畫面上的二維碼，訂單會在這裡打開。',
      scanLooking: '正在尋找訂單…',
      scanForeign: '這不是本店的取餐碼。請改用編號、姓名或電話搜尋。',
      scanNoMatch: '找不到符合的訂單。請確認顧客的畫面，或改用姓名或電話搜尋。',
      emptyActive: '目前沒有進行中的訂單',
      emptyActiveHint: '顧客送出訂單後會立即顯示在這裡。',
      newBadge: '新訂單',
      scheduledBadge: '已預約',
      // ---- unused, and kept for the orders rework --------------------------
      // `OrderCard.timePressure()` computes the six below and nothing renders
      // them, which makes them read as leftovers to a dead-key sweep. Nine more
      // in this block have no reader at all: `newBadge`, `target`, `placed`,
      // `readyIn`, `ready`, `overdue`, `pickupAt`, `readyNow` and
      // `cancelWithReason`. The merchant orders page is being rebuilt and this is
      // the wording it will want, so they are marked rather than deleted.
      waitingTooLong: '等待過久',
      notCollected: '未取餐',
      codeLabel: '訂單代碼',
      showStatusTab: '只顯示{status}的訂單',
      filterByChip: '篩選{chip}',
      clearChip: '清除{chip}篩選',
      showingChip: '篩選：{chip}',
      clearFilter: '清除篩選',
      showingOfLive: { one: '{shown} / {total} 進行中', other: '{shown} / {total} 進行中' },
      pastEstimate: '已超過預計時間',
      target: '目標時間',
      placed: '下單時間',
      readyIn: '剩餘時間',
      ready: '可取餐',
      overdue: '已逾時',
      pickupAt: '取餐時間',
      startBy: '須於 {when} 開始製作',
      bookedInsidePrep: '下單時已過了開始時間',
      bookedInsidePrepHint: '這張訂單送達時已超過應開始製作時間，廚房沒有充裕時間準備。',
      note: '備註：{note}',
      accept: '接單',
      startPreparing: '開始製作',
      markReady: '標記為可取餐',
      handedOver: '已交付',
      handedOverFull: '已交付給顧客',
      readyNow: '現在',
      detailTitle: '訂單 {number}',
      openOrder: '開啟訂單 {number}',
      callCustomer: '撥打電話',
      cancelOrder: '取消訂單',
      cancelTitle: '要取消訂單 {number} 嗎？',
      cancelSubtitle: '顧客會立即看到取消通知。庫存將退回。',
      cancelWarning: '此操作無法復原',
      cancelWarningHint: '若餐點已經製作，請考慮改為在櫃檯告知顧客。',
      cancelReason: '原因（選填）',
      cancelReasonPlaceholder: '顧客來電取消',
      cancelReasonHint: '會記錄在訂單上並顯示於紀錄中。',
      cancelKeep: '保留訂單',
      cancelConfirm: '取消訂單',
      cancelledToast: '訂單 {number} 已取消',
      statusToast: '訂單 {number} — {status}',
      statusRunToast: '{count} 張單 — {status}',
      // ---- 錢，不是狀態 ------------------------------------------------------
      // 現金在櫃檯收，而「什麼時候收」本身是一件事實：排隊的客人可以在打包前先付，
      // 熟客也可以下一次再結。訂單板上只有這一個標籤會說這件事，而且只在已付款時
      // 出現——未付款的單不會有任何標記，號碼旁的金額就是該收的錢。
      paid: '已付款',
      unpaid: '未付款',
      payment: '付款',
      notPaid: '尚未付款',
      paidAt: '已於 {time} 付款',
      markPaid: '標記為已付款',
      markUnpaid: '標記為未付款',
      paidToast: '訂單 {number} — 已付款',
      unpaidToast: '訂單 {number} — 未付款',
      timeline: '時間軸',
      estimate: '預估',
      estimateScheduled: '預約取餐，預約時間為 {when}。',
      estimateQuoted: '以 {model} 模型預估 {minutes} 分鐘{samples}{queue}。',
      estimateSamples: '（{count} 筆相似訂單）',
      estimateQueue: '，佇列前方還有 {count} 筆',
      receiptSent: '收據已寄至 {email}。',
      receiptNone: '顧客未提供電子郵件，因此未寄送收據。',
      loading: '載入訂單…',
      stepPlaced: '已下單',
      stepAccepted: '已接單',
      stepPreparing: '製作中',
      stepReady: '可取餐',
      stepCollected: '已取餐',
    },

    menu: {
      itemCount: '{count} 個項目',
      itemCountOne: '1 個項目',
      moveTitleOne: '將這個項目移至…',
      deleteItemsTitleOne: '刪除這個項目？',
      deletedCount: '已刪除 {count} 個項目',
      deleteItemsTitle: '刪除 {count} 個項目？',
      deleteItemsMessage: '它們會立即從菜單及顧客端移除，且無法復原。',
      undo: '復原',
      undoDone: '已放回原來分類',
      select: '選取',
      selectDone: '完成',
      selectedCount: '已選 {count} 項',
      moveTo: '移至…',
      moveTitle: '將 {count} 項移至…',
      moveFrom: '來自',
      moveAlreadyHere: '已有 {count} 項',
      moveNothingToMove: '沒有需要移動的餐點',
      moveDoneOne: '已移至 {category}',
      moveDone: '已將 {count} 項移至 {category}',
      reorder: '調整順序',
      reorderUp: '將 {name} 上移',
      reorderDown: '將 {name} 下移',
      extrasDuplicateTitle: '有兩個選項名稱相同',
      extrasDuplicate: '廚房會看到兩個「{name}」，無法分辨。',
      extrasUpToTooHigh: '有群組的最多可選數超過選項數。',
      extrasUpToTooManyTitle: '超過選項數量',
      extrasUpToTooMany: '此群組有 {count} 個選項，最多只能選 {count} 項。',
      extrasUpTo: '最多可選',
      extrasUpToHint: '最多可選幾項。預設為全部 {count} 項。',
      extrasAbsoluteOneOnly: '只能選一項，因為「即為餐點價格」的選項無法與其他選項相加。若要可累加的選項，請選擇上方的選項。',
      extrasNameRequiredTitle: '這個選項群組沒有名稱',
      extrasNameRequired: '請輸入名稱，否則其選項不會被儲存。',
      extrasIntentExtras: '不會 — 不決定餐點價格',
      extrasSetsPriceConsequence: '此餐點將為 {price} — 最便宜的選項。上方價格將被忽略。',
      extrasHowMany: '可以選幾項？',
      extrasHowManyOne: '一項',
      extrasHowManyAny: '任意數量',
      extrasPriceQuestion: '此群組會影響價格嗎？',
      extrasIntentSets: '會 — 每項即餐點價格',
      priceFromGroup: '此餐點價格由下方選項決定。',
      extrasAllFreeTitle: '此群組的所有選項皆為免費',
      extrasAllFree: '這些選項決定價格，因此此餐點将為免費。若這些選擇不影響價格（如甜度、冰量），請關閉「這些選項決定價格」。',
      extrasTitle: '加購選項',
      extrasHint: '餐點本身的選擇 — 甜度、冰量或配料。',
      extrasName: '群組名稱',
      extrasNamePlaceholder: '配料',
      extrasSetsPrice: '這些選項決定價格',
      extrasSetsPriceHint: '用於份量：所選即為餐點價格，而非加價。',
      extrasRequired: '顧客必須選擇',
      extrasChoiceName: '選項',
      extrasChoicePrice: '價格（{currency}）',
      extrasAdd: '新增群組',
      extrasAddChoice: '新增選項',
      extrasRemove: '移除群組',
      extrasRemoveChoice: '移除選項',
      scanLimited: '今日 {limit} 次掃描已用完，明天再試，或手動新增餐點。',
      scanAdd: '將 {count} 項加入菜單',
      scanAddNone: '未選取任何項目',
      scanAdding: '正在加入第 {done} / {total} 項…',
      scanAdded: '已加入 {count} 項',
      scanExists: '菜單中已有此項目',
      scanNoPrice: '讀不到價格 — 請手動加入',
      scanUnreachable: '掃描服務未啟動 — 請執行 bun scripts/scan-api.ts。',
      scanTitle: '掃描菜單',
      scanSubtitle: '拍下印刷菜單，確認讀取的內容。',
      scanChoose: '選擇照片',
      scanReading: '正在讀取菜單…',
      scanFound: '{categories} 個分類，{items} 項餐點',
      scanFailed: '無法讀取這張照片。',
      hasOptions: '此餐點有多個選項',
      hasOptionsHint: '開啟後可依份量、種類或配料定價；上方價格將由選項決定。',
      priceFromOptions: '由下方選項決定。',
      optionsRequired: '請至少新增一個選項，並填寫名稱與價格。',
      optionPriceRequired: '每個選項都需要價格。',
      minQuantity: '最少數量',
      minQuantityHint: '維持 1 表示沒有限制。若餐點以 5 個為單位出售，請設為 5 — 顧客會從 5 開始且不能少於 5。',
      options: '選項',
      optionsHint: '選填。為此餐點提供不同選擇 — 大小、雞肉或豬肉、半份或整份 — 各有自己的價格。留空則以上方價格整份出售。',
      optionName: '選項',
      optionNamePlaceholder: '小份',
      optionPrice: '價格（{currency}）',
      removeOption: '移除 {name}',
      remove: '移除',
      addOption: '新增選項',
      title: '菜單',
      hiddenCount: '{count} 項已隱藏',
      newCategory: '新增分類',
      newItem: '新增餐點',
      uncategorised: '未分類',
      noPhoto: '沒有照片',
      hidden: '已隱藏',
      scheduledBadge: '時段限定',
      left: '剩 {count} 份',
      offPercent: '折 {value}%',
      offAmount: '折 {value}',
      stock: '庫存',
      actionsFor: '{name} 的操作',
      hideFromCustomers: '對顧客隱藏',
      showToCustomers: '向顧客顯示',
      empty: '這裡還沒有餐點',
      emptyHint: '新增第一道餐點後，會立即出現在顧客菜單上。',
      emptyNoCategories: '還沒有分類',
      emptyNoCategoriesHint: '餐點需要歸入分類，先建立一個分類，就可以開始新增餐點。',
      loading: '載入你的菜單…',
      noRestaurant: '尚未選擇餐廳',
      categoryNew: '新增分類',
      categoryRename: '重新命名分類',
      categoryName: '名稱',
      categoryNamePlaceholder: '主餐',
      categoryDescriptionPlaceholder: '現點現做的飯麵類',
      categoryHint: '分類用於將餐點分組顯示在顧客菜單上。刪除分類會保留餐點並移至「未分類」。',
      deleteItemTitle: '要刪除 {name} 嗎？',
      deleteItemMessage: '歷史訂單會保留這道餐點的快照，因此銷售紀錄不受影響。此操作無法復原。',
      deleteCategoryMessage: '此分類中的餐點會被保留並移至「未分類」。此操作無法復原。',
      addItemToCategory: '新增餐點',
      addToMenu: '加入菜單',
      stockTitle: '{name} 的庫存',
      stockSubtitle: '數量歸零時，餐點會自動對顧客隱藏。',
      stockRemaining: '剩餘數量',
      saved: '{name} 已儲存',
      categorySaved: '分類已儲存',
      deleted: '{name} 已刪除',
      nowOnMenu: '{name} 已上架',
      nowHidden: '{name} 已隱藏',
      stockSet: '{name}：庫存 {count} 份',
      tabDetails: '基本資料',
      tabAvailability: '供應時段',
      tabOffers: '優惠',
      nameLabel: '名稱',
      namePlaceholder: '娘惹叻沙',
      descriptionPlaceholder: '內容物是什麼？如何供應？',
      priceLabel: '價格（{currency}）',
      pricePlaceholder: '180',
      priceHint: '顧客未含稅與優惠前的價格為 {price}。',
      categoryLabel: '分類',
      prepLabel: '備餐時間（分鐘）',
      prepHint: '這道餐點為訂單增加的製作時間。設為 0 則使用餐廳預設的 {minutes} 分鐘。',
      visibleLabel: '在菜單上顯示',
      visibleHint: '不需要刪除也能隱藏餐點 — 適合季節性商品。',
      trackStockLabel: '追蹤庫存',
      trackStockHint: '數量歸零時自動隱藏餐點，訂單取消時自動退回庫存。',
      inStockLabel: '目前庫存',
      replacePhoto: '更換照片',
      addPhoto: '新增照片',
      removePhoto: '移除照片',
      keepPhoto: '保留照片',
      photoHint: '點擊圖片可選擇其他照片。',
      servingTimesInfo: '供應時段',
      servingTimesInfoHint:
        '未設定時段時，只要餐廳營業即可點購。設定時段可限定於特定時段供應，例如早餐品項。',
      alwaysAvailable: '全時段供應',
      alwaysAvailableHint: '這道餐點在營業時間內隨時可點。',
      addServingTime: '新增供應時段',
      from: '開始',
      until: '結束',
      removeWindow: '移除',
      addAnotherDay: '新增其他日期',
      copyToEveryDay: '複製到每一天',
      clearAll: '全部清除',
      timesNote: '時間以 {timezone} 為準。結束時間早於開始時間代表跨夜供應。',
      saveServingTimes: '儲存供應時段',
      saveFirst: '請先儲存餐點',
      saveFirstHint: '優惠是綁定在餐點上的，請先將餐點加入菜單再設定折扣。',
      currentOffers: '目前與預約中的優惠',
      currentOffersHint: '若有多個優惠重疊，顧客會自動取得最優惠的價格。',
      noOffers: '這道餐點沒有優惠。',
      runningNow: '進行中',
      finished: '已結束',
      scheduledLabel: '已排程',
      scheduleOffer: '排定優惠',
      offerType: '類型',
      percentage: '百分比',
      amountOff: '金額（{currency}）',
      percentOff: '折扣百分比',
      amountOffLabel: '折抵金額（{currency}）',
      offerLabel: '標籤',
      offerLabelPlaceholder: '歡樂時光',
      offerLabelHint: '會顯示在顧客菜單上。',
      starts: '開始時間',
      ends: '結束時間',
      restaurantDateTime: '餐廳當地時間（{timezone}）',
      customerPays: '優惠期間顧客實付',
      scheduleOfferButton: '排定優惠',
      offerScheduled: '優惠已排定',
      invalidDiscount: '請輸入大於零的折扣金額',
      percentTooHigh: '百分比不可超過 100',
      offerEndBeforeStart: '結束時間必須晚於開始時間',
      nameRequired: '請為餐點命名',
      priceRequired: '請輸入價格，例如 180',
      inventoryRequired: '請輸入可供應的數量',
      categoryFirstName: '請輸入名稱',

      // 餐點編輯器。每個區塊都有標題與一行說明。
      cardDetailsHint: '顧客在菜單上看到的內容。',
      cardPrice: '價格',
      cardPriceHint: '這道餐點的單一價格。',
      cardKitchen: '廚房',
      cardKitchenHint: '製作方式與最少訂購量。',
      cardChoices: '選項',
      cardChoicesHint: '選項群組，以及顧客每組可選幾項。群組可以決定價格（如份量），也可以加價。',
      cardAvailability: '供應時段',
      cardAvailabilityHint: '留空表示全天供應。',
      cardOffer: '優惠',
      cardOfferHint: '為這道餐點設定限時折扣。',
      cardCategoryHint: '只能屬於一個分類，所以全部分類都會顯示。再點一下即可移動。',
      cardPhoto: '照片',
      cardPhotoHint: '多數商家都沒有照片。值得提醒一下。',
      onMenuLabel: '在菜單上',
      onMenuHint: '隱藏的餐點仍留在菜單中，只是不會顯示。',
      trackLeftLabel: '追蹤剩餘數量',
      trackLeftHint: '數量歸零時停止接單。',
      groupPickOne: '選一項',
      groupPickAny: '可選多項',
      groupPickUpTo: '最多選 {count} 項',
      groupRequired: '必選',
      groupOptional: '選填',
      groupKindSets: '決定價格',
      groupKindFree: '不影響價格',
      groupKindAdds: '加價',
      groupShownRow: '每個選項都是餐點的一種版本，各自定價。在菜單上以橫列顯示。',
      groupShownChips: '在菜單上以餐點旁的加購選項顯示。',
      groupShownAdds: '每個選項都會加到餐點價格上。以標籤方式顯示。',
      groupEdit: '編輯',
      groupDone: '完成',
    },

    hours: {
      title: '營業時間',
      intro: '所有時間以 {timezone} 為準。顧客僅能在營業時間內下單或預約。',
      closedEveryDay: '你每天都設定為休息',
      closedEveryDayHint: '顧客將無法下單。請至少設定一天營業日。',
      weekly: '每週營業時間',
      opens: '開始營業',
      closes: '結束營業',
      closesBeforeItOpens: '結束時間早於開始時間。跨夜營業請改在{day}新增時段。',
      closesBeforeItOpensOneOff: '結束時間早於開始時間。跨夜的單日例外請設於接著的日期。',
      addWindow: '新增營業時段',
      removeWindow: '移除',
      overlappingWindows: '{day} 有兩個時段重疊，請縮短其中一個。',
      saveHours: '儲存營業時間',
      unsaved: '尚未儲存的變更',
      exceptions: '例外日期',
      exceptionsHint: '國定假日與單次異動。此處設定的日期會取代當天的每週規則。',
      noExceptions: '沒有例外日期 — 每週營業時間固定適用。',
      addException: '新增例外日期',
      closedAllDay: '全天休息',
      closedAllDayHint: '關閉則改為指定不同的營業時間。',
      exceptionNotePlaceholder: '包場活動，暫停營業',
      addExceptionButton: '新增例外',
      removeTitle: '要移除這個例外日期嗎？',
      removeMessage: '該日期將恢復套用每週營業時間。',
      saved: '營業時間已儲存',
      exceptionSaved: '例外日期已儲存',
      exceptionRemoved: '例外日期已移除',
      invalidDate: '請以 YYYY-MM-DD 格式輸入日期',
      loading: '載入營業時間…',
      noRestaurant: '尚未選擇餐廳',
      hourRange: '{open} – {close}',
      overnightSuffix: '（+1 天）',
      weekdayLong0: '星期日',
      weekdayLong1: '星期一',
      weekdayLong2: '星期二',
      weekdayLong3: '星期三',
      weekdayLong4: '星期四',
      weekdayLong5: '星期五',
      weekdayLong6: '星期六',
    },

    tables: {
      title: '桌位與櫃檯',
      intro: '為顧客可能用餐或等候的每個位置列印一組條碼，就能將訂單歸屬到對應位置。',
      neverExpire: '這些條碼不會自動失效',
      add: '新增條碼',
      neverExpireHint:
        '訂單無論如何都隸屬於餐廳 — 條碼只是告訴廚房顧客在哪裡。若貼紙遺失，請停用該條碼。',
      empty: '尚無條碼',
      emptyHint: '新增櫃檯與各桌位，即可產生可列印的 QR Code。',
      seats: '{count} 個座位',
      active: '啟用中',
      disabled: '已停用',
      enable: '啟用',
      disable: '停用',
      addTitle: '新增條碼',
      addSubtitle: '這張貼紙會放在哪裡？',
      labelField: '標籤',
      labelPlaceholder: '7 號桌',
      seatsField: '座位數',
      seatsPlaceholder: '4',
      seatsHint: '選填。櫃檯或候位區可留空。',
      create: '建立條碼',
      deleteTitle: '要刪除 {label} 嗎？',
      deleteMessage: '列印的貼紙會立即失效。歷史訂單仍會保留相關紀錄。',
      added: '已新增 {label}',
      deleted: '已刪除 {label}',
      enabled: '已重新啟用 {label}',
      disabledToast: '已停用 {label}',
      printSubtitle: '掃描點餐，到櫃檯付款',
      printThis: '列印此頁',
      copyUrl: '複製連結',
      copyFailed: '無法複製連結。',
      loading: '載入桌位條碼…',
      noRestaurant: '尚未選擇餐廳',
    },

    settings: {
      blockedHint: '你已封鎖的號碼。解除封鎖前，他們無法向你下單。',
      unblockedToast: '該號碼可以再次下單。',
      unblockFailedToast: '無法解除封鎖。',
      blockedTitle: '已封鎖的號碼',
      blockedEmpty: '目前沒有封鎖任何號碼。',
      blockedSince: '{date} 封鎖',
      unblock: '解除封鎖',
      webAddress: '網址',
      webAddressHint: '你的菜單連結。無法在此更改，否則顧客手上的連結將會失效。',
      title: '設定',
      details: '餐廳資料',
      detailsHint: '顧客看到的餐廳資訊。',
      cover: '封面',
      coverHint: '顯示在菜單最上方。',
      coverImage: '封面圖片',
      restaurantName: '餐廳名稱',
      descriptionLabel: '說明',
      phoneLabel: '電話',
      phoneHint: '顧客可點按此號碼致電你。',
      addressLabel: '地址',
      addressHint: '顧客取餐的地點。',
      currencyHint:
        '菜單與報表上的價格都使用建立餐廳時設定的貨幣。無法在此更改，因為所有價格會以新貨幣顯示而不會換算。',
      paused: '目前已暫停接單',
      pausedHint: '顧客仍可瀏覽菜單，但無法送出訂單。廚房準備好時請重新開啟。',
      acceptOrders: '接受訂單',
      autoAccept: '立即開始製作',
      autoAcceptHint:
        '新訂單會直接進入廚房，不必先接單。預約訂單仍需要你接單。',
      acceptOrdersHint: '尖峰時段或盤點時可關閉。既有訂單不受影響。',
      minNotice: '最短前置時間',
      minNoticeHint: '顧客最早可預約的取餐時間。',
      bookAhead: '最多可預約',
      slotSpacing: '時段間隔',
      slotSpacingHint: '提供選擇的取餐時間間隔。',
      timezone: '時區（地區）',
      timezoneHint: '取餐時間以此時區為準。無法在此更改，否則顧客已預訂的取餐時間會隨之改變。',
      pickupTimes: '取餐時間',
      pickupTimesHint: '這些設定決定顧客可以選擇的取餐時間。',
      money: '金額',
      moneyHint: '菜單使用的貨幣，以及結帳時加收的稅金。',
      taxRate: '稅率',
      taxRateHint: '以折扣後的小計計算。',
      taxExample: '以 {price} 的餐點為例，顧客實付 {total}。若價格已含稅請設為 0。',
      prepEstimates: '備餐時間預估',
      prepEstimatesHint: '在我們從完成訂單學到你的實際備餐時間之前，會先採用這些設定。',
      learnedFrom: '依 {count} 筆完成訂單計算',
      stillLearning: '仍在學習中',
      medianPrep: '一般 {duration}',
      overhead: '每筆訂單追加時間',
      overheadHint: '每筆訂單都會加上：擺盤、裝袋、排隊。',
      fastest: '最快可能時間',
      fastestHint: '預估時間不會比這個短。',
      slowest: '最慢可能時間',
      slowestHint: '預計時間的上限。需要更久的訂單仍會顯示 {minutes} 分鐘。',
      capBindsTitle: '預計時間一律顯示 {cap} 分鐘',
      capBindsHint:
        '你最慢的訂單實際需要超過 {cap} 分鐘，模型無法反映真實時間，結帳時也不會標示廚房做不到的取餐時間。',
      tellingTitle: '待接單的訂單',
      tellingHint: '當顧客還沒被接單時，多久之後告知顧客——訂單板也是在這時候把未接單的訂單標為延誤。',
      acceptGrace: '等候訊息顯示於',
      minutesUnit: '分鐘',
      daysUnit: '天',
      percentUnit: '%',
      acceptGraceBasis: '由何時起算',
      acceptGracePlaced: '下單時',
      acceptGraceEstimate: '預計時間',
      acceptGraceNewOrders: '新訂單',
      acceptGraceScheduledOrders: '預約訂單',
      acceptGraceOutcomePlaced: '下單後 {minutes} 分鐘顯示訊息。',
      acceptGraceOutcomeEstimate: '預計時間結束後 {minutes} 分鐘顯示訊息。',
      acceptGraceOutcomeScheduled: '最遲開始時間後 {minutes} 分鐘顯示訊息。',
      chaseTitle: '在櫃檯等待',
      chaseHint: '可取餐的訂單等的是顧客，不是廚房。超過這個時間，訂單板會把它標為延誤，提醒櫃檯打電話。',
      chaseWindow: '超過多久打電話給顧客',
      chaseOutcome: '可取餐的訂單在出餐後 {minutes} 分鐘會被標為延誤。',
      cookRoundTitle: '烹調輪次',
      cookRoundHint:
        '「待製作」頁面會把多長時間內的餐點分成同一輪。每輪從隊列中最早的取餐時間開始，經過這個長度後結束。',
      cookRoundWindow: '每輪涵蓋',
      cookRoundOutcome: '取餐時間相差在 {minutes} 分鐘內的餐點，會分成同一批烹調。',
      ordersAtOnce: '同時處理的訂單數',
      ordersAtOnceHint: '廚房可同時製作的訂單數量。待處理的訂單越多，預估時間越長。',
      perDishInfo: '餐點時間如何相加',
      perDishInfoHint:
        '每道餐點在菜單編輯器中都有自己的備餐時間。我們會先加上你的追加時間，再加上最久那道餐點的全部時間，最後加上其餘餐點的部分時間。',
      receipts: '顧客收據',
      receiptsHint: '顧客在結帳時填寫電子郵件，系統會自動寄送。',
      emailActive: '電子郵件寄送功能正常',
      emailNotConfigured: '尚未設定電子郵件寄送',
      sentCount: '已寄出 {count} 封',
      receiptsQueued: '收據已排入佇列，尚未寄出',
      receiptsQueuedHint:
        '有 {count} 封收據等待中。新增電子郵件服務金鑰即可開始寄送 — 內容皆已產生並儲存。',
      receiptsFailed: '有 {count} 封收據無法寄送',
      receiptsFailedHint: '請檢查寄件地址與 API 金鑰。',
      lastReceipt: '最後一封收據寄送於 {when}。',
      noReceiptsYet: '尚未寄出任何收據。',
      checkingEmail: '檢查電子郵件寄送狀態…',
      verificationTitle: '驗證',
      verificationHint: 'Yummy 會先核對餐廳的名稱、地址與聯絡方式，才會標記為已驗證。',
      verificationSince: '自 {when} 起已驗證。',
      sessions: '登入工作階段',
      sessionsHint: '會登出所有裝置，包括這一部。',
      signOutEverywhere: '登出所有裝置',
      signOutEverywhereTitle: '登出所有裝置？',
      signOutEverywhereMessage:
        '所有裝置都會登出，包括這一部。其他裝置會在一小時內登出。',
      signOutEverywhereFailed: '無法登出所有裝置，請檢查網路連線後再試一次。',
      discard: '捨棄變更',
      save: '儲存設定',
      saved: '設定已儲存',
      loading: '載入設定…',
      noRestaurant: '尚未選擇餐廳',
      loadFailed: '無法載入設定',
    },

    appearance: {
      light: '淺色',
      dark: '深色',
      system: '跟隨系統',
    },
  },
};
